import React, { useState, useContext, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CartContext } from "../../../components/cartContext";
import ThemeToggler from "../../../components/themeToggler";
import { ThemeContext } from "../../../components/themeContext";
import { usePartner } from "../../../contexts/PartnerContext";

// Import default images (fallbacks)
import DefaultLogo from "../../../assets/images/cloudVantage Logo.png";
import DefaultIcon from "../../../assets/images/cloudVantage Icon.png";
import WhiteDefaultLogo from "../../../assets/images/cloudVantage Logo White.png";
import WhiteDefaultIcon from "../../../assets/images/cloudVantage Icon White.png";
import cart from "../../../assets/svg-icons/cart.svg";
import menu from "../../../assets/svg-icons/menu.svg";
import menuWhite from "../../../assets/svg-icons/menuWhite.svg";
import menuBrand from "../../../assets/svg-icons/menuBrand.svg";
import projectsFolder from "../../../assets/svg-icons/folder.svg";
import close from "../../../assets/svg-icons/closeBrand.svg";
import arrow from "../../../assets/svg-icons/arrow.svg";
import hamburger from "../../../assets/svg-icons/hamburger-menu.svg";
import add from "../../../assets/svg-icons/addWhite.svg";

import { useDispatch, useSelector } from "react-redux";
import Modal from "../../../components/modal";
import Input from "../../../components/input";
import TextArea from "../../../components/textArea";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  createProject,
  listUserProjects,
  setSelectedProject,
  switchUserProject,
} from "../../../hooks/local/userReducer";
import Spinner from "../../../components/spinner";
import { capitalizeFirstLetter, isDevelopment } from "../../../hooks/constants";

const WhiteLabelTopBar = () => {

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useContext(ThemeContext);
  const { totalPrice, currency } = useContext(CartContext);
  const { partnerInfo, currentSubdomain, loading } = usePartner();

  const [serviceVisible, setServiceVisible] = useState(false);
  const [projectsVisible, setProjectsVisible] = useState(false);
  const [smallScreenMenu, setSmallScreenMenu] = useState(false);
  const [addNewProjectDiv, setAddNewProjectDiv] = useState(false);
  const [showPartnerAdminModal, setShowPartnerAdminModal] = useState(false);

  const userProjectList = useSelector((state) => state.user.userProjectList);
  const selectedProject = useSelector((state) => state.user.selectedProject);
  const userLoading = useSelector((state) => state.user).loading;

  const getPartnerLogo = () => {
    return partnerInfo?.logo ? partnerInfo.logo : (theme === "dark" ? WhiteDefaultLogo : DefaultLogo);
  };

  const getPartnerIcon = () => {
    return partnerInfo?.icon ? partnerInfo.icon : (theme === "dark" ? WhiteDefaultIcon : DefaultIcon);
  };

  const getPartnerName = () => {
    return capitalizeFirstLetter(partnerInfo?.name) || "CloudVantage";
  };

  useEffect(() => {
    if (userProjectList && userProjectList.length > 0 && !selectedProject) {
      dispatch(setSelectedProject(userProjectList[0]));
    }
  }, [dispatch, userProjectList, selectedProject]);

  const handleProjectSelection = async (userSelectedProject) => {
    await dispatch(setSelectedProject(userSelectedProject));
    const { payload } = await dispatch(
      switchUserProject(userSelectedProject.id),
    );

    if (payload.statuscode === "00") {
      const currentPath = location.pathname;
      if (currentPath.includes("manageSecurityGroups")) {
        window.location.href = "/securityGroups";
      } else if (
        currentPath === "/client-area" ||
        currentPath === "/securityGroups" ||
        currentPath === "/configure-vps" ||
        currentPath === "/client-area/volumes"
      ) {
        window.location.reload();
      }
    }
    closeProjects();
  };

  const toggleServices = () => {
    setServiceVisible(true);
    setSmallScreenMenu(false);
  };

  const closeServices = () => {
    setServiceVisible(false);
  };

  const toggleProjects = () => {
    setProjectsVisible(true);
    setSmallScreenMenu(false);
  };

  const closeProjects = () => {
    setProjectsVisible(false);
  };

  const toggleSmallScreenMenu = () => {
    setSmallScreenMenu(true);
  };

  const closeSmallScreenMenu = () => {
    setSmallScreenMenu(false);
  };

  const switchToMain = () => {
    if(isDevelopment()) {
      // Remove all partner-related parameters in development
      const url = new URL(window.location);
      url.searchParams.delete('partner');
      url.searchParams.delete('subdomain');
      url.searchParams.delete('admin');
      window.location.href = url.toString();
    } else {
      // In production, redirect to main CloudVantage site
      window.location.href = `https://cloudvantage.com.ng`;
    }
  };

  const createNewProject = useFormik({
    initialValues: {
      projectDescription: "",
      projectName: "",
    },
    validationSchema: Yup.object({
      projectName: Yup.string().required("Project Name cannot be empty"),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { projectName, projectDescription } = values;
      let createProjectData = {
        name: projectName,
        description: projectDescription,
      };
      const { payload } = await dispatch(
        createProject({ createProjectData, navigate }),
      );
      if (payload.statuscode === "00") {
        await dispatch(listUserProjects());
        resetForm();
        setAddNewProjectDiv(false);
        setProjectsVisible(true);
        navigate("/client-area");
      }
    },
  });

  const [isNetworksOpen, setIsNetworksOpen] = useState(false);
  const [isVolumesOpen, setIsVolumesOpen] = useState(false);

  const networkSublinks = [
    { path: "/networks", label: "Networks" },
    { path: "/routers", label: "Routers" },
    { path: "/floating-ips", label: "Floating IPs" },
    { path: "/manageLoadBalancers", label: "Load Balancers" },
  ];

  const volumeSublinks = [
    { path: "/client-area/volumes", label: "Volumes" },
    { path: "/manageVolumeSnapshots", label: "Volume Snapshots" },
    { path: "/manageVolumeBackups", label: "Volume Backups" },
  ];

  return (
    <div className="relative">
      <Spinner loading={userLoading || loading} />
      
      {/* Partner Context Bar */}
      <div className="bg-gradient-to-b from-blue-800 via-blue-900 to-blue-950 py-2 px-4 text-center">
        <div className="text-white text-sm font-medium">
         {getPartnerName()} • Portal
        </div>
      </div>

      <div 
        className="bg-gradient-to-r from-slate-50 to-indigo-50 dark:from-slate-900 dark:to-indigo-900/30 py-4 px-4 md:pe-10 border-b border-indigo-200 dark:border-indigo-800 dark:text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <Link to={"/client-area"}>
              <img
                src={getPartnerLogo()}
                alt={`${getPartnerName()} Logo`}
                className="hidden lg:block h-[18px]"
              />
            </Link>
            <Link to={"/client-area"}>
              <img
                src={getPartnerIcon()}
                alt={`${getPartnerName()} Icon`}
                className="lg:hidden h-6"
              />
            </Link>
          </div>
          <div className="flex gap-3 items-center">
            {partnerInfo && (
              <div
                className="hidden md:block text-sm cursor-pointer px-3 py-2 rounded text-primaryBlue dark:text-indigo-400 hover:bg-indigo-100 transition-colors"
                onClick={() => setShowPartnerAdminModal(true)}
              >
                Admin Panel
              </div>
            )}
            <div
              onClick={toggleProjects}
              className="hidden md:flex text-sm items-center px-3 py-2 cursor-pointer text-primaryBlue rounded transition-colors"
            >
              <span className="">
                {selectedProject ? selectedProject.name : ""}
              </span>
              <img
                src={theme === "dark" ? menuWhite : menu}
                alt=""
                className="h-[18px] ml-2 brightness-0 saturate-100 invert-[.35] sepia-[1] saturate-[5] hue-rotate-[225deg]"
              />
            </div>
            <Link
              to={"/faq"}
              className="hidden md:block text-sm px-3 py-2 cursor-pointer text-primaryBlue rounded transition-colors"
            >
              <span className="">FAQs</span>
            </Link>
            <div className="md:hidden" onClick={toggleSmallScreenMenu}>
              <img src={hamburger} alt="" className="h-5 brightness-0 saturate-100 invert-[.35] sepia-[1] saturate-[5] hue-rotate-[225deg]" />
            </div>
            <Link
              to={"/client-area/configure-vps"}
              className="flex gap-[10px] items-center rounded-lg p-2 md:p-[14px] bg-primaryBlue hover:text-white transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <img src={cart} alt="" className="h-4" />
              <span className="text-xs text-white font-medium">               
                {totalPrice > 0
                  ? `${currency === "USD" ? "USD " : "₦ "}${totalPrice.toLocaleString()}`
                  : "0"}
              </span>
            </Link>
            <ThemeToggler />
          </div>
        </div>
      </div>

      {showPartnerAdminModal && (
        <div className="w-full fixed h-screen bg-black/70 top-0 px-4 flex items-end md:items-center justify-center z-50">
          <div className="bg-white dark:bg-[#141D26] rounded-xl w-full md:w-[40%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto p-4 md:p-6 border border-indigo-200 dark:border-primaryBlue">
            <div onClick={() => setShowPartnerAdminModal(false)} className="cursor-pointer mb-4 text-right text-[#c4c4c4] hover:text-primaryBlue transition-colors">[Close]</div>
            <div className="grid gap-4 text-sm mb-6">
              <span className="font-bold text-lg text-primaryBlue dark:text-indigo-300">Access Admin Panel</span>
              <span>Switch to the general admin panel to continue enjoying our services</span>
            </div>
            <div className="flex gap-4">
              <button
                onClick={switchToMain}
                className="py-4 px-10 rounded-lg text-white text-sm bg-primaryBlue hover:bg-primaryBlue transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Go to Admin Panel
              </button>
              <button
                onClick={() => setShowPartnerAdminModal(false)}
                className="py-4 px-10 rounded-lg border border-primaryBlue hover:text-white hover:bg-primaryBlue transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {projectsVisible && (
        <div className="w-full fixed h-screen bg-black/70 top-0 px-4 flex items-end md:items-center justify-center z-40">
          <div className="bg-white dark:bg-[#141D26] rounded-xl w-full md:w-[30%] dark:text-white max-h-[80%] mb-4 md:mb-0 overflow-auto border border-indigo-200 dark:border-indigo-800">
            <div className="flex justify-between items-center text-sm p-6 border-b border-indigo-200 dark:border-indigo-800">
              <div className="flex gap-2 items-center text-indigo-700 dark:text-indigo-300">
                <img src={projectsFolder} alt="" className="h-[18px] brightness-0 saturate-100 invert-[.35] sepia-[1] saturate-[5] hue-rotate-[225deg]" />
                <span className="dark:text-white">My projects</span>
              </div>
              <img
                src={close}
                onClick={closeProjects}
                className="h-5 cursor-pointer hover:opacity-70 transition-opacity"
                alt=""
              />
            </div>
            <div className="grid text-xs px-4 pb-4 pt-2">
              {userProjectList &&
                userProjectList.length > 0 &&
                userProjectList.map((val, key) => {
                  return (
                    <div
                      key={key}
                      onClick={() => {
                        handleProjectSelection(val);
                      }}
                      className="flex items-center justify-between hover:rounded border-b border-indigo-100 dark:border-indigo-800 last:border-none py-4 px-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 transition-all cursor-pointer"
                    >
                      <span className="text-indigo-700 dark:text-indigo-300">{val.name}</span>
                      <span>
                        <img src={arrow} alt="" className="-rotate-90 brightness-0 saturate-100 invert-[.35] sepia-[1] saturate-[5] hue-rotate-[225deg]" />
                      </span>
                    </div>
                  );
                })}

              <div
                onClick={() => {
                  setAddNewProjectDiv(true);
                  setProjectsVisible(false);
                }}
                className="flex items-center justify-between bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 py-4 px-3 mt-4 rounded-lg cursor-pointer transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                <span className="text-white font-medium">Create a new project</span>
                <img src={add} alt="" className="h-5" />
              </div>
            </div>
          </div>
        </div>
      )}

      <Modal
        isVisible={addNewProjectDiv}
        title={"Create New Project"}
        onClose={() => {
          setAddNewProjectDiv(false);
          setProjectsVisible(true);
        }}
      >
        <form onSubmit={createNewProject.handleSubmit}>
          <div className="grid gap-4 mt-8">
            <Input
              label={"Project name"}
              type={"text"}
              name={"projectName"}
              value={createNewProject.values.projectName}
              onBlur={createNewProject.handleBlur}
              onChange={createNewProject.handleChange}
              onError={
                createNewProject.errors.projectName &&
                createNewProject.touched.projectName
                  ? createNewProject.errors.projectName
                  : null
              }
            />
            <TextArea
              label={"Project description"}
              type={"text"}
              noOfRow={"3"}
              name={"projectDescription"}
              value={createNewProject.values.projectDescription}
              onBlur={createNewProject.handleBlur}
              onChange={createNewProject.handleChange}
              onError={
                createNewProject.errors.projectDescription &&
                createNewProject.touched.projectDescription
                  ? createNewProject.errors.projectDescription
                  : null
              }
            />
            <div className="grid">
              <button
                type="submit"
                className="text-sm md:text-xs font-medium text-white rounded-lg px-8 py-4 cursor-pointer bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Create Project
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default WhiteLabelTopBar;

